/**
 * Bundle & Save — Dynamic Post-Add-to-Cart Upsell Page
 * Determines upsell products based on the purchased product handle,
 * fetches product data via Shopify AJAX API, and renders cards dynamically.
 */
(function () {
  'use strict';

  var PHOTO_KEYS = ['Pet Photo', 'Upload', 'Photo'];
  var SUPABASE_PHOTO_BASE = 'https://igwekemyevllvmgqdmtl.supabase.co/storage/v1/object/public/user-uploads/';

  /* ═══════════════════════════════════════════
     UPSELL MAPPING TABLE
     Each key is a product handle. Value is an array of 4 upsell slots:
       [0] = matching style mug
       [1] = complementary high-value (portrait / blanket / weekender — whichever source isn't)
       [2] = matching style low-value (pillow, mouse pad, poker cards) — fallback: peeking mug
       [3] = matching style low-value — fallback: woven blanket
     Each slot: { handle, badge } or null to skip
     ═══════════════════════════════════════════ */

  var PEEKING_MUG = { handle: 'custom-peeking-pet-colorful-mug-1', badge: 'Best Seller' };
  var WOVEN_BLANKET = { handle: 'custom-relaxed-pet-woven-blanket-1', badge: 'Premium' };

  var STYLE_GROUPS = {
    modern: {
      mug: { handle: 'modern-custom-pet-colorful-mug', badge: 'Popular' },
      blanket: { handle: 'custom-pet-blanket', badge: 'Cozy Gift' },
      portrait: { handle: 'custom-one-pet-portrait', badge: 'Best Seller' },
      weekender: { handle: 'custom-weekender-bag', badge: 'Travel' },
      pillow: { handle: 'custom-throw-pillow-18x18', badge: 'Home Decor' },
      mousePad: { handle: 'custom-round-mouse-pad', badge: 'Desk Buddy' },
      pokerCards: { handle: 'custom-poker-playing-cards', badge: 'Fun Gift' }
    },
    memorial: {
      mug: { handle: 'custom-pet-memorial-mug', badge: 'Memorial' },
      blanketHalo: { handle: 'memorial-custom-pet-blanket-with-halo-1', badge: 'Memorial' },
      blanketRainbow: { handle: 'memorial-custom-pet-blanket-with-rainbow', badge: 'Memorial' },
      portraitHalo: { handle: 'custom-memorial-pet-portrait-with-halo', badge: 'Memorial' },
      portraitRainbow: { handle: 'custom-memorial-pet-portrait-1', badge: 'Memorial' }
    },
    floral: {
      mug: { handle: 'custom-floral-pet-colorful-mug', badge: 'Floral' },
      blanketSpring: { handle: 'spring-floral-custom-pet-blanket', badge: 'Floral' },
      blanketCozy: { handle: 'cozy-floral-custom-pet-blanket', badge: 'Cozy' },
      portraitSpring: { handle: 'spring-floral-custom-pet-portrait', badge: 'Floral' },
      portraitCozy: { handle: 'cozy-watercolor-custom-pet-portrait', badge: 'Watercolor' }
    },
    pearlEarring: {
      mug: { handle: 'custom-pearl-earring-pet-mug', badge: 'Royal' },
      blanket: { handle: 'pearl-earring-custom-pet-blanket', badge: 'Royal' },
      portrait: { handle: 'pearl-earring-custom-pet-portrait', badge: 'Royal' }
    },
    aristocratic: {
      mug: { handle: 'custom-aristocratic-pet-mug', badge: 'Royal' },
      blanket: { handle: 'aristocratic-custom-pet-blanket', badge: 'Royal' },
      portrait: { handle: 'aristocratic-custom-pet-portrait-1', badge: 'Royal' },
      pokerCards: { handle: 'custom-aristocratic-poker-playing-cards-1', badge: 'Fun Gift' },
      weekender: { handle: 'aristocratic-weekender-bag', badge: 'Travel' },
      pillow: { handle: 'aristocratic-throw-pillow-18x18', badge: 'Home Decor' }
    },
    vintageRoyal: {
      mug: { handle: 'custom-vintage-royal-pet-mug', badge: 'Royal' },
      blanket: { handle: 'vintage-royal-custom-pet-blanket', badge: 'Royal' },
      portrait: { handle: 'vintage-royal-custom-pet-portrait', badge: 'Royal' },
      pokerCards: { handle: 'custom-vintage-royal-poker-playing-cards', badge: 'Fun Gift' }
    },
    royal: {
      mug: { handle: 'custom-royal-pet-mug', badge: 'Royal' },
      blanket: { handle: 'royal-custom-pet-blanket', badge: 'Royal' },
      portrait: { handle: 'royal-custom-pet-portrait', badge: 'Royal' },
      pokerCards: { handle: 'custom-royal-poker-playing-cards', badge: 'Fun Gift' },
      weekender: { handle: 'royal-weekender-bag', badge: 'Travel' },
      pillow: { handle: 'royal-throw-pillow-18x18', badge: 'Home Decor' }
    },
    rococo: {
      mug: { handle: 'custom-tulip-rococo-custom-vintage-royal-pet-mug', badge: 'Rococo' },
      blanket: { handle: 'custom-rococo-tulip-bouquet-blanket', badge: 'Rococo' },
      portrait: { handle: 'custom-tulip-rococo-spring-floral-custom-pet-portrait', badge: 'Rococo' }
    },
    tulipWreath: {
      mug: { handle: 'custom-tulip-wreath-mug', badge: 'Floral' },
      blanket: { handle: 'tulip-wreath-custom-pet-blanket', badge: 'Floral' },
      weekender: { handle: 'custom-tulip-wreath-weekender-bag', badge: 'Floral' },
      pillow: { handle: 'custom-tulip-wreath-throw-pillow-18x18', badge: 'Home Decor' },
      mousePad: { handle: 'custom-tulip-wreath-mouse-pad', badge: 'Desk Buddy' }
    },
    herbWreath: {
      mug: { handle: 'custom-herb-wreath-mug', badge: 'Floral' },
      blanket: { handle: 'herb-wreath-custom-pet-blanket', badge: 'Floral' },
      portrait: { handle: 'herb-wreath-custom-pet-portrait', badge: 'Floral' },
      weekender: { handle: 'custom-herb-wreath-weekender-bag', badge: 'Floral' },
      pillow: { handle: 'custom-herb-wreath-throw-pillow-18x18', badge: 'Home Decor' },
      mousePad: { handle: 'custom-herb-wreath-mouse-pad', badge: 'Desk Buddy' }
    },
    roseWreath: {
      mug: { handle: 'custom-tulip-wreath-mug-1', badge: 'Floral' },
      blanket: { handle: 'rose-wreath-custom-pet-blanket', badge: 'Floral' },
      portrait: { handle: 'rose-wreath-custom-pet-portrait', badge: 'Floral' },
      weekender: { handle: 'custom-rose-wreath-weekender-bag', badge: 'Floral' },
      pillow: { handle: 'custom-rose-wreath-throw-pillow-18x18', badge: 'Home Decor' },
      mousePad: { handle: 'custom-rose-wreath-mouse-pad', badge: 'Desk Buddy' }
    },
    colorPencil: {
      mug: { handle: 'custom-color-pencil-mug', badge: 'Hand-Drawn' },
      blanket: { handle: 'color-pencil-custom-pet-blanket', badge: 'Cozy Gift' },
      portrait: { handle: 'color-pencil-custom-pet-portrait', badge: 'Hand-Drawn' },
      weekender: { handle: 'color-pencil-weekender-bag', badge: 'Travel' },
      pillow: { handle: 'color-pencil-throw-pillow-18x18', badge: 'Home Decor' }
    },
    soccerCard: {
      mug: { handle: 'soccer-card-pet-mug', badge: 'Game Day' },
      portrait: { handle: 'soccer-card-pet-portrait', badge: 'Fan Favorite' },
      pokerCards: { handle: 'soccer-card-poker-playing-cards', badge: 'Fun Gift' }
    },
    soccerPoster: {
      mug: { handle: 'soccer-poster-pet-mug', badge: 'Game Day' },
      portrait: { handle: 'soccer-poster-pet-portrait', badge: 'Fan Favorite' },
      pokerCards: { handle: 'soccer-poster-poker-playing-cards', badge: 'Fun Gift' }
    }
  };

  /** Map product handle → array of upsell slots */
  var UPSELL_MAP = {
    // ── Modern Portraits ──
    'custom-one-pet-portrait':    function () { return [STYLE_GROUPS.modern.mug, STYLE_GROUPS.modern.blanket, STYLE_GROUPS.modern.pillow, STYLE_GROUPS.modern.mousePad]; },
    'custom-two-pet-portrait-1':  function () { return [STYLE_GROUPS.modern.mug, STYLE_GROUPS.modern.blanket, STYLE_GROUPS.modern.pillow, STYLE_GROUPS.modern.mousePad]; },
    'custom-three-pet-portrait':  function () { return [STYLE_GROUPS.modern.mug, STYLE_GROUPS.modern.blanket, STYLE_GROUPS.modern.pillow, STYLE_GROUPS.modern.mousePad]; },
    'custom-four-pet-portrait-1': function () { return [STYLE_GROUPS.modern.mug, STYLE_GROUPS.modern.blanket, STYLE_GROUPS.modern.pillow, STYLE_GROUPS.modern.mousePad]; },

    // ── Modern Blanket ──
    'custom-pet-blanket': function () { return [STYLE_GROUPS.modern.mug, STYLE_GROUPS.modern.portrait, STYLE_GROUPS.modern.pillow, STYLE_GROUPS.modern.mousePad]; },

    // ── Modern Weekender ──
    'custom-weekender-bag': function () { return [STYLE_GROUPS.modern.mug, STYLE_GROUPS.modern.portrait, STYLE_GROUPS.modern.pillow, STYLE_GROUPS.modern.mousePad]; },

    // ── Memorial Portraits ──
    'custom-memorial-pet-portrait-with-halo': function () { return [STYLE_GROUPS.memorial.mug, STYLE_GROUPS.memorial.blanketHalo, PEEKING_MUG, WOVEN_BLANKET]; },
    'custom-memorial-pet-portrait':           function () { return [STYLE_GROUPS.memorial.mug, STYLE_GROUPS.memorial.blanketHalo, PEEKING_MUG, WOVEN_BLANKET]; },
    'custom-memorial-pet-portrait-1':         function () { return [STYLE_GROUPS.memorial.mug, STYLE_GROUPS.memorial.blanketRainbow, PEEKING_MUG, WOVEN_BLANKET]; },

    // ── Memorial Blankets ──
    'memorial-custom-pet-blanket-with-halo-1':  function () { return [STYLE_GROUPS.memorial.mug, STYLE_GROUPS.memorial.portraitHalo, PEEKING_MUG, WOVEN_BLANKET]; },
    'memorial-custom-pet-blanket-with-rainbow': function () { return [STYLE_GROUPS.memorial.mug, STYLE_GROUPS.memorial.portraitRainbow, PEEKING_MUG, WOVEN_BLANKET]; },

    // ── Watercolor / Floral Portraits ──
    'spring-floral-custom-pet-portrait':        function () { return [STYLE_GROUPS.floral.mug, STYLE_GROUPS.floral.blanketSpring, PEEKING_MUG, WOVEN_BLANKET]; },
    'cozy-watercolor-custom-pet-portrait':      function () { return [STYLE_GROUPS.floral.mug, STYLE_GROUPS.floral.blanketCozy, PEEKING_MUG, WOVEN_BLANKET]; },
    'cartoon-watercolor-custom-pet-portrait-2': function () { return [STYLE_GROUPS.floral.mug, STYLE_GROUPS.floral.blanketCozy, PEEKING_MUG, WOVEN_BLANKET]; },

    // ── Floral Blankets ──
    'spring-floral-custom-pet-blanket':   function () { return [STYLE_GROUPS.floral.mug, STYLE_GROUPS.floral.portraitSpring, PEEKING_MUG, WOVEN_BLANKET]; },
    'cozy-floral-custom-pet-blanket':     function () { return [STYLE_GROUPS.floral.mug, STYLE_GROUPS.floral.portraitCozy, PEEKING_MUG, WOVEN_BLANKET]; },
    'custom-relaxed-pet-woven-blanket-1': function () { return [STYLE_GROUPS.floral.mug, STYLE_GROUPS.floral.portraitCozy, PEEKING_MUG, null]; },

    // ── Pearl Earring ──
    'pearl-earring-custom-pet-portrait': function () { return [STYLE_GROUPS.pearlEarring.mug, STYLE_GROUPS.pearlEarring.blanket, PEEKING_MUG, WOVEN_BLANKET]; },
    'pearl-earring-custom-pet-blanket':  function () { return [STYLE_GROUPS.pearlEarring.mug, STYLE_GROUPS.pearlEarring.portrait, PEEKING_MUG, WOVEN_BLANKET]; },

    // ── Aristocratic ──
    'aristocratic-custom-pet-portrait-1':   function () { return [STYLE_GROUPS.aristocratic.mug, STYLE_GROUPS.aristocratic.blanket, STYLE_GROUPS.aristocratic.pokerCards, PEEKING_MUG]; },
    'aristocratic-custom-two-pet-portrait': function () { return [STYLE_GROUPS.aristocratic.mug, STYLE_GROUPS.aristocratic.blanket, STYLE_GROUPS.aristocratic.pokerCards, PEEKING_MUG]; },
    'aristocratic-custom-pet-blanket':      function () { return [STYLE_GROUPS.aristocratic.mug, STYLE_GROUPS.aristocratic.portrait, STYLE_GROUPS.aristocratic.pokerCards, PEEKING_MUG]; },

    // ── Cartoon (singleton — borrows aristocratic upsells until cartoon style grows) ──
    'cartoon-custom-pet-portrait': function () { return [STYLE_GROUPS.aristocratic.mug, STYLE_GROUPS.aristocratic.blanket, STYLE_GROUPS.aristocratic.pokerCards, PEEKING_MUG]; },

    // ── Vintage Royal ──
    'vintage-royal-custom-pet-portrait':     function () { return [STYLE_GROUPS.vintageRoyal.mug, STYLE_GROUPS.vintageRoyal.blanket, STYLE_GROUPS.vintageRoyal.pokerCards, PEEKING_MUG]; },
    'vintage-royal-custom-two-pet-portrait': function () { return [STYLE_GROUPS.vintageRoyal.mug, STYLE_GROUPS.vintageRoyal.blanket, STYLE_GROUPS.vintageRoyal.pokerCards, PEEKING_MUG]; },
    'vintage-royal-custom-pet-blanket':      function () { return [STYLE_GROUPS.vintageRoyal.mug, STYLE_GROUPS.vintageRoyal.portrait, STYLE_GROUPS.vintageRoyal.pokerCards, PEEKING_MUG]; },

    // ── Royal (no two-pet portrait by design) ──
    'royal-custom-pet-portrait':     function () { return [STYLE_GROUPS.royal.mug, STYLE_GROUPS.royal.blanket, STYLE_GROUPS.royal.pokerCards, PEEKING_MUG]; },
    'royal-custom-pet-blanket':      function () { return [STYLE_GROUPS.royal.mug, STYLE_GROUPS.royal.portrait, STYLE_GROUPS.royal.pokerCards, PEEKING_MUG]; },

    // ── Aristocratic / Royal Weekender Bags (upsell triggers — handle contains "weekender") ──
    'aristocratic-weekender-bag': function () { return [STYLE_GROUPS.aristocratic.mug, STYLE_GROUPS.aristocratic.portrait, STYLE_GROUPS.aristocratic.pillow, STYLE_GROUPS.aristocratic.pokerCards]; },
    'royal-weekender-bag':        function () { return [STYLE_GROUPS.royal.mug, STYLE_GROUPS.royal.portrait, STYLE_GROUPS.royal.pillow, STYLE_GROUPS.royal.pokerCards]; },

    // ── Rococo ──
    'custom-tulip-rococo-spring-floral-custom-pet-portrait': function () { return [STYLE_GROUPS.rococo.mug, STYLE_GROUPS.rococo.blanket, PEEKING_MUG, WOVEN_BLANKET]; },
    'custom-rococo-tulip-bouquet-blanket':                   function () { return [STYLE_GROUPS.rococo.mug, STYLE_GROUPS.rococo.portrait, PEEKING_MUG, WOVEN_BLANKET]; },

    // ── Tulip Wreath (no portrait — weekender↔blanket complement) ──
    'tulip-wreath-custom-pet-blanket':   function () { return [STYLE_GROUPS.tulipWreath.mug, STYLE_GROUPS.tulipWreath.weekender, STYLE_GROUPS.tulipWreath.pillow, STYLE_GROUPS.tulipWreath.mousePad]; },
    'custom-tulip-wreath-weekender-bag': function () { return [STYLE_GROUPS.tulipWreath.mug, STYLE_GROUPS.tulipWreath.blanket, STYLE_GROUPS.tulipWreath.pillow, STYLE_GROUPS.tulipWreath.mousePad]; },

    // ── Herb Wreath (portrait↔blanket complement) ──
    'herb-wreath-custom-pet-portrait':  function () { return [STYLE_GROUPS.herbWreath.mug, STYLE_GROUPS.herbWreath.blanket, STYLE_GROUPS.herbWreath.pillow, STYLE_GROUPS.herbWreath.mousePad]; },
    'herb-wreath-custom-pet-blanket':   function () { return [STYLE_GROUPS.herbWreath.mug, STYLE_GROUPS.herbWreath.portrait, STYLE_GROUPS.herbWreath.pillow, STYLE_GROUPS.herbWreath.mousePad]; },
    'custom-herb-wreath-weekender-bag': function () { return [STYLE_GROUPS.herbWreath.mug, STYLE_GROUPS.herbWreath.portrait, STYLE_GROUPS.herbWreath.pillow, STYLE_GROUPS.herbWreath.mousePad]; },

    // ── Rose Wreath (portrait↔blanket complement) ──
    'rose-wreath-custom-pet-portrait':  function () { return [STYLE_GROUPS.roseWreath.mug, STYLE_GROUPS.roseWreath.blanket, STYLE_GROUPS.roseWreath.pillow, STYLE_GROUPS.roseWreath.mousePad]; },
    'rose-wreath-custom-pet-blanket':   function () { return [STYLE_GROUPS.roseWreath.mug, STYLE_GROUPS.roseWreath.portrait, STYLE_GROUPS.roseWreath.pillow, STYLE_GROUPS.roseWreath.mousePad]; },
    'custom-rose-wreath-weekender-bag': function () { return [STYLE_GROUPS.roseWreath.mug, STYLE_GROUPS.roseWreath.portrait, STYLE_GROUPS.roseWreath.pillow, STYLE_GROUPS.roseWreath.mousePad]; },

    // ── Color Pencil (portrait↔blanket complement; single low-value = pillow, woven blanket fallback) ──
    'color-pencil-custom-pet-portrait': function () { return [STYLE_GROUPS.colorPencil.mug, STYLE_GROUPS.colorPencil.blanket, STYLE_GROUPS.colorPencil.pillow, WOVEN_BLANKET]; },
    'color-pencil-custom-pet-blanket':  function () { return [STYLE_GROUPS.colorPencil.mug, STYLE_GROUPS.colorPencil.portrait, STYLE_GROUPS.colorPencil.pillow, WOVEN_BLANKET]; },
    'color-pencil-weekender-bag':       function () { return [STYLE_GROUPS.colorPencil.mug, STYLE_GROUPS.colorPencil.portrait, STYLE_GROUPS.colorPencil.pillow, WOVEN_BLANKET]; },

    // ── Soccer (portrait triggers only; no blanket/weekender — mug + poker matching, then fallbacks) ──
    'soccer-card-pet-portrait':   function () { return [STYLE_GROUPS.soccerCard.mug, STYLE_GROUPS.soccerCard.pokerCards, PEEKING_MUG, WOVEN_BLANKET]; },
    'soccer-poster-pet-portrait': function () { return [STYLE_GROUPS.soccerPoster.mug, STYLE_GROUPS.soccerPoster.pokerCards, PEEKING_MUG, WOVEN_BLANKET]; }
  };

  var MUG_DISCOUNT = 0.5; // 50% off for mugs in the bundle

  /** Known mug handles from STYLE_GROUPS — used as fallback when Mugs collection fetch fails */
  var KNOWN_MUG_HANDLES = new Set([
    PEEKING_MUG.handle,
    STYLE_GROUPS.modern.mug.handle,
    STYLE_GROUPS.memorial.mug.handle,
    STYLE_GROUPS.floral.mug.handle,
    STYLE_GROUPS.pearlEarring.mug.handle,
    STYLE_GROUPS.aristocratic.mug.handle,
    STYLE_GROUPS.vintageRoyal.mug.handle,
    STYLE_GROUPS.royal.mug.handle,
    STYLE_GROUPS.rococo.mug.handle,
    STYLE_GROUPS.tulipWreath.mug.handle,
    STYLE_GROUPS.herbWreath.mug.handle,
    STYLE_GROUPS.roseWreath.mug.handle,
    STYLE_GROUPS.colorPencil.mug.handle,
    STYLE_GROUPS.soccerCard.mug.handle,
    STYLE_GROUPS.soccerPoster.mug.handle
  ]);

  var cartItems = [];
  var petPhotos = [];
  var productDataMap = {};
  var mugProductIds = new Set();
  var sourceOrderId = '';

  /** Determine which product triggered the upsell */
  function getSourceHandle() {
    var params = new URLSearchParams(window.location.search);
    return params.get('from') || '';
  }

  /** Get upsell slots for the source product */
  function getUpsellSlots(handle) {
    var mapFn = UPSELL_MAP[handle];
    if (mapFn) return mapFn();

    // Newly routed online preview products use their existing style family.
    // Never recommend the product the customer just bought.
    var style = Object.values(STYLE_GROUPS).find(function (group) {
      return Object.values(group).some(function (product) { return product && product.handle === handle; });
    });
    if (style) {
      return Object.values(style).filter(function (product) {
        return product && product.handle && product.handle !== handle;
      }).slice(0, 4);
    }

    // Fallback: try to match by keyword in the handle
    if (handle.indexOf('memorial') !== -1 && handle.indexOf('blanket') !== -1) {
      return UPSELL_MAP['memorial-custom-pet-blanket-with-halo-1']();
    }
    if (handle.indexOf('memorial') !== -1) {
      return UPSELL_MAP['custom-memorial-pet-portrait']();
    }
    if (handle.indexOf('blanket') !== -1) {
      return UPSELL_MAP['custom-pet-blanket']();
    }
    if (handle.indexOf('portrait') !== -1) {
      return UPSELL_MAP['custom-one-pet-portrait']();
    }
    if (handle.indexOf('weekender') !== -1) {
      return UPSELL_MAP['custom-weekender-bag']();
    }
    // Other supported preview sources still get a useful bundle page.
    return handle ? [STYLE_GROUPS.modern.portrait, STYLE_GROUPS.modern.blanket,
      STYLE_GROUPS.modern.pillow, STYLE_GROUPS.modern.mousePad] : null;
  }

  /** Fetch product IDs in the "Mugs" collection */
  function fetchMugCollection() {
    return fetch('/collections/mugs/products.json?limit=250')
      .then(function (res) { return res.ok ? res.json() : { products: [] }; })
      .then(function (data) {
        (data.products || []).forEach(function (p) { mugProductIds.add(p.id); });
      })
      .catch(function () { /* collection may not exist yet */ });
  }

  /** Check if a product is a mug (collection membership, or fallback to known handles) */
  function isMugProduct(productId) {
    if (mugProductIds.has(productId)) return true;
    var product = productDataMap[productId];
    return !!(product && KNOWN_MUG_HANDLES.has(product.handle));
  }

  /** Apply mug bundle discount to a price */
  function applyMugDiscount(price) {
    return (parseFloat(price) * (1 - MUG_DISCOUNT)).toFixed(2);
  }

  /** Fire-and-forget PostHog capture — telemetry must never block or break the page */
  function captureEvent(eventName, properties) {
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(eventName, properties);
      }
    } catch (_) {}
  }

  /** Show (message) or clear (null) the inline error slot on an upsell card */
  function setCardError(card, message) {
    var el = card.querySelector('[data-bundle-error]');
    if (!el) return;
    if (message) {
      el.textContent = message;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  /** Preserve existing decimal-price/card contracts while keeping catalog images private. */
  function adaptStorefrontProduct(data) {
    var product = data && data.product;
    var roles = data && data.storefrontImages;
    if (data?.schemaVersion !== 1 || roles?.schemaVersion !== 1 || !product?.id ||
        String(roles.productId) !== String(product.id) || !Array.isArray(roles.media) ||
        !Array.isArray(product.variants) || roles.variantCount !== product.variants.length) {
      throw new Error('Invalid storefront product payload');
    }
    product.images = roles.media;
    product.image = roles.media[0] || null;
    product.variants = product.variants.map(function (variant) {
      var mediaId = roles.variantMedia[String(variant.id)];
      var image = roles.media.find(function (media) { return String(media.id) === String(mediaId); });
      if (roles.policy === 'custom_design_v1' && !image) throw new Error('Missing marketing image');
      return Object.assign({}, variant, {
        price: Number(variant.price) / 100,
        compare_at_price: variant.compare_at_price == null ? null : Number(variant.compare_at_price) / 100,
        featured_image: image || null
      });
    });
    return product;
  }

  var storefrontProductRequests = new Map();
  /** Theme-owned Liquid payload, in the same locale and preview theme as this page. */
  function fetchProduct(handle) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = new URL(root + 'products/' + encodeURIComponent(handle), window.location.origin);
    url.searchParams.set('view', 'storefront-data');
    var previewTheme = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
    if (storefrontProductRequests.has(url.href)) return storefrontProductRequests.get(url.href);
    var request = fetch(url.href, { signal: AbortSignal.timeout(15000) })
      .then(function (res) {
        if (!res.ok) {
          var failure = new Error('Product image payload HTTP ' + res.status);
          failure.status = res.status;
          throw failure;
        }
        return res.json().then(adaptStorefrontProduct);
      })
      .catch(function (e) {
        storefrontProductRequests.delete(url.href);
        captureEvent('bundle_upsell_product_load_failed', {
          handle: handle,
          status: typeof e?.status === 'number' ? e.status : null,
          message: (e && e.message) || String(e)
        });
        return null;
      });
    storefrontProductRequests.set(url.href, request);
    return request;
  }

  /** Render a single upsell card */
  function renderCard(product, badge) {
    productDataMap[product.id] = product;
    var isMug = isMugProduct(product.id);

    // Override badge for mugs
    if (isMug) badge = '50% Off';

    var firstVariant = null;
    if (product.variants && product.variants.length > 0) {
      firstVariant = product.variants[0];
    }

    var card = document.createElement('div');
    card.className = 'bundle-card';
    card.setAttribute('data-upsell-card', '');
    card.setAttribute('data-default-variant-id', firstVariant ? firstVariant.id : '');
    card.setAttribute('data-product-id', product.id);
    if (isMug) card.setAttribute('data-is-mug', '');

    // Collect all images
    var images = product.images && product.images.length > 0 ? product.images : [];
    if (images.length === 0 && product.image) {
      images = [product.image];
    }

    // Build gallery
    var html = '<div class="bundle-card__gallery" data-bundle-gallery data-gallery-index="0">';
    if (badge) {
      html += '<span class="bundle-card__badge">' + escapeHtml(badge) + '</span>';
    }

    // Prev arrow
    if (images.length > 1) {
      html += '<button type="button" class="bundle-card__gallery-arrow bundle-card__gallery-arrow--prev" data-gallery-prev aria-label="Previous image" disabled>';
      html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>';
      html += '</button>';
    }

    // Images
    html += '<div class="bundle-card__images">';
    for (var i = 0; i < images.length; i++) {
      var imgSrc = images[i].src || images[i];
      // Request appropriately sized images from Shopify CDN (cards are ~450px max)
      if (typeof imgSrc === 'string' && imgSrc.indexOf('cdn.shopify.com') !== -1) {
        imgSrc += (imgSrc.indexOf('?') !== -1 ? '&' : '?') + 'width=600';
      }
      var imgId = images[i].id || '';
      html += '<img class="bundle-card__image' + (i === 0 ? ' bundle-card__image--active' : '') + '"';
      html += ' src="' + escapeHtml(imgSrc) + '"';
      html += ' alt="' + escapeHtml(product.title) + '"';
      html += ' width="600" height="600"';
      html += ' loading="' + (i === 0 ? 'eager' : 'lazy') + '"';
      html += ' data-image-index="' + i + '"';
      if (imgId) html += ' data-image-id="' + imgId + '"';
      html += '>';
    }
    html += '</div>';

    // Next arrow
    if (images.length > 1) {
      html += '<button type="button" class="bundle-card__gallery-arrow bundle-card__gallery-arrow--next" data-gallery-next aria-label="Next image">';
      html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>';
      html += '</button>';
    }

    // Dots indicator
    if (images.length > 1) {
      html += '<div class="bundle-card__gallery-dots">';
      for (var i = 0; i < images.length; i++) {
        html += '<span class="bundle-card__gallery-dot' + (i === 0 ? ' bundle-card__gallery-dot--active' : '') + '" data-dot-index="' + i + '"></span>';
      }
      html += '</div>';
    }
    html += '</div>'; // end gallery

    // Card body
    html += '<div class="bundle-card__body">';
    html += '<h3 class="bundle-card__title">' + escapeHtml(product.title) + '</h3>';

    // Price — Shopify AJAX API returns prices in dollars (e.g. "29.99"), NOT cents
    var displayPrice = firstVariant ? firstVariant.price : 0;
    var comparePrice = null;

    if (isMug && firstVariant) {
      // Mugs get 50% off — show original price as strikethrough
      comparePrice = firstVariant.price;
      displayPrice = applyMugDiscount(firstVariant.price);
    } else if (firstVariant && firstVariant.compare_at_price &&
      parseFloat(firstVariant.compare_at_price) > parseFloat(firstVariant.price)) {
      comparePrice = firstVariant.compare_at_price;
    }

    html += '<div class="bundle-card__price-row">';
    html += '<span class="bundle-card__price" data-upsell-price>' + formatMoney(displayPrice) + '</span>';
    if (comparePrice) {
      html += '<span class="bundle-card__compare-price">' + formatMoney(comparePrice) + '</span>';
    }
    html += '</div>';

    // Variant selector (skip if only default variant)
    var hasRealVariants = product.variants && product.variants.length > 1 ||
      (product.variants && product.variants.length === 1 && product.variants[0].title !== 'Default Title');
    if (hasRealVariants) {
      // Use the first option name as label (e.g. "Size", "Color")
      var optionName = 'Option';
      if (product.options && product.options.length > 0) {
        var opt = product.options[0];
        optionName = (typeof opt === 'object' && opt.name) ? opt.name : String(opt);
      }

      html += '<div class="bundle-card__variant-group">';
      html += '<label class="bundle-card__variant-label" for="upsell-variant-' + product.id + '">' + escapeHtml(optionName) + '</label>';
      html += '<select class="bundle-card__variant-select" id="upsell-variant-' + product.id + '" data-upsell-variant data-product-id="' + product.id + '">';
      for (var i = 0; i < product.variants.length; i++) {
        var v = product.variants[i];
        var sel = (firstVariant && v.id === firstVariant.id) ? ' selected' : '';
        var imageId = (v.featured_image && v.featured_image.id) ? v.featured_image.id : '';
        var optPrice = isMug ? applyMugDiscount(v.price) : v.price;
        var optCompare = isMug ? v.price : (v.compare_at_price || '');
        html += '<option value="' + v.id + '" data-price="' + optPrice + '"';
        if (optCompare) html += ' data-compare-price="' + optCompare + '"';
        if (imageId) html += ' data-image-id="' + imageId + '"';
        html += sel + '>';
        html += escapeHtml(v.title) + ' — ' + formatMoney(optPrice);
        html += '</option>';
      }
      html += '</select></div>';
    }

    html += '<div data-upsell-photo-selector data-product-handle="' + escapeHtml(product.handle) + '" hidden></div>';
    html += '<button class="bundle-card__btn" type="button" data-upsell-add-to-cart>Add to Bundle \u2192</button>';
    html += '<p class="bundle-card__error" data-bundle-error role="alert" hidden></p>';
    html += '</div>';

    card.innerHTML = html;
    return card;
  }

  /** Format price — AJAX API returns dollars (e.g. "29.99"), not cents */
  function formatMoney(price) {
    return '$' + parseFloat(price).toFixed(2);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /** Navigate gallery to a specific image index */
  function setGalleryIndex(gallery, index) {
    var images = gallery.querySelectorAll('.bundle-card__image');
    var dots = gallery.querySelectorAll('.bundle-card__gallery-dot');
    var total = images.length;
    if (index < 0 || index >= total) return;

    gallery.setAttribute('data-gallery-index', index);

    for (var i = 0; i < images.length; i++) {
      images[i].classList.toggle('bundle-card__image--active', i === index);
    }
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('bundle-card__gallery-dot--active', i === index);
    }

    // Update arrow states
    var prevBtn = gallery.querySelector('[data-gallery-prev]');
    var nextBtn = gallery.querySelector('[data-gallery-next]');
    if (prevBtn) prevBtn.disabled = (index === 0);
    if (nextBtn) nextBtn.disabled = (index === total - 1);
  }

  /** Scroll gallery to image matching a specific image ID */
  function scrollGalleryToImageId(gallery, imageId) {
    if (!imageId) return;
    var img = gallery.querySelector('.bundle-card__image[data-image-id="' + imageId + '"]');
    if (img) {
      var index = parseInt(img.getAttribute('data-image-index'), 10);
      setGalleryIndex(gallery, index);
    }
  }

  /** Main init — fetch cart + upsell products, render cards */
  async function init() {
    var container = document.querySelector('.bundle-upsell');
    var grid = document.querySelector('[data-upsell-grid]');
    if (!container || !grid) return;

    // Show loading state while fetching products
    grid.innerHTML = '<div class="bundle-upsell__loading"><div class="bundle-upsell__spinner"></div><p>Preparing your personalized products\u2026</p></div>';

    try {
      var res = await fetch('/cart.json');
      if (!res.ok) {
        // Leaving the spinner up forever is a dead end mid-funnel — track it
        // and reuse the no-mapping fallback so the shopper keeps moving.
        captureEvent('bundle_upsell_init_failed', { phase: 'cart_fetch', status: res.status });
        window.location.href = '/cart';
        return;
      }
      var cart = await res.json();
      cartItems = cart.items || [];

      if (cartItems.length === 0) {
        container.innerHTML = '<div style="text-align:center;padding:60px 20px;"><h2 style="font-family:var(--font-heading--family);margin-bottom:16px;">Your cart is empty</h2><p style="margin-bottom:24px;opacity:0.7;">Start by creating your custom pet portrait!</p><a href="/collections/portraits" class="button button-primary" style="display:inline-block;padding:12px 32px;background:#5a7a5c;color:#fff;border-radius:var(--style-border-radius-buttons-primary);text-decoration:none;">Start Creating</a></div>';
        return;
      }

      petPhotos = extractPetPhotos(cartItems);

      // This is a count refresh, not a new add. Keep the bundle page visible.
      var initialDrawer = document.querySelector('cart-drawer-component');
      var drawerAutoOpen = initialDrawer && initialDrawer.hasAttribute('auto-open');
      if (drawerAutoOpen) initialDrawer.removeAttribute('auto-open');
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { itemCount: cart.item_count } },
      }));
      if (drawerAutoOpen) initialDrawer.setAttribute('auto-open', '');

      // Determine source product and get upsell slots
      var sourceHandle = getSourceHandle();
      var slots = sourceHandle ? getUpsellSlots(sourceHandle) : null;

      // Extract source Order ID so upsell items share the same ID for fulfillment
      for (var i = cartItems.length - 1; i >= 0; i--) {
        var props = cartItems[i].properties;
        if (props && props['Order ID'] && (!sourceHandle || cartItems[i].handle === sourceHandle)) {
          sourceOrderId = props['Order ID'];
          break;
        }
      }
      // Fallback: use most recent cart item's Order ID
      if (!sourceOrderId) {
        for (var i = cartItems.length - 1; i >= 0; i--) {
          if (cartItems[i].properties && cartItems[i].properties['Order ID']) {
            sourceOrderId = cartItems[i].properties['Order ID'];
            break;
          }
        }
      }

      // Fallback: try to detect from cart items
      if (!slots) {
        for (var i = cartItems.length - 1; i >= 0; i--) {
          var itemHandle = cartItems[i].handle;
          slots = getUpsellSlots(itemHandle);
          if (slots) break;
        }
      }

      if (!slots || slots.length === 0) {
        // No mapping found — redirect to cart
        window.location.href = '/cart';
        return;
      }

      // Filter out null slots and fetch all products + mug collection in parallel
      var validSlots = slots.filter(function (s) { return s && s.handle !== sourceHandle; });
      var productPromises = validSlots.map(function (slot) { return fetchProduct(slot.handle); });
      var results = await Promise.all([Promise.all(productPromises), fetchMugCollection()]);
      var products = results[0];

      // Every upsell product failed to load (each captured in fetchProduct) —
      // an empty grid is a dead end, so fall back to the cart instead.
      if (validSlots.length > 0 && !products.some(function (p) { return !!p; })) {
        window.location.href = '/cart';
        return;
      }

      // Clear loading state and render cards
      grid.innerHTML = '';
      for (var i = 0; i < products.length; i++) {
        if (products[i]) {
          var card = renderCard(products[i], validSlots[i].badge);
          grid.appendChild(card);
        }
      }

      // After cards are in DOM, render photo selectors and bind events
      renderPhotoSelectors();
      bindEvents();

    } catch (e) {
      console.error('Bundle upsell: init failed', e);
      captureEvent('bundle_upsell_init_failed', {
        phase: 'exception',
        message: (e && e.message) || String(e)
      });
      window.location.href = '/cart';
    }
  }

  /** Extract pet photo URLs from cart line item properties */
  function extractPetPhotos(items) {
    var photos = [];
    var seenUrls = new Set();
    for (var idx = 0; idx < items.length; idx++) {
      var item = items[idx];
      if (!item.properties) continue;
      var itemId = item.properties._id || item.properties.id || item.key;
      var entries = Object.entries(item.properties);
      for (var j = 0; j < entries.length; j++) {
        var key = entries[j][0], value = entries[j][1];
        if (!value || typeof value !== 'string') continue;
        var isPhotoKey = PHOTO_KEYS.some(function (k) { return key.indexOf(k) !== -1; });
        // Detect photo paths: either a full URL (legacy) or a short path like "uuid/timestamp.png"
        var isPhotoPath = isPhotoKey && value.length > 0 && (value.indexOf('http') === 0 || value.indexOf('/uploads/') !== -1 || /^[0-9a-f-]+\/\d+\.\w+$/.test(value));
        if (isPhotoPath && !seenUrls.has(value)) {
          seenUrls.add(value);
          // Reconstruct full URL if it's a short path
          var fullUrl = value.indexOf('http') === 0 ? value : SUPABASE_PHOTO_BASE + value;
          var petName = findPetName(item.properties, key);
          if (!petName && item.properties._parentId) {
            var parent = items.find(function (i) { return i.properties && i.properties._id === item.properties._parentId; });
            if (parent) petName = findPetName(parent.properties, key);
          }
          photos.push({ id: itemId, url: fullUrl, name: petName || item.product_title, key: item.key });
        }
      }
    }
    return photos;
  }

  function findPetName(properties, photoKey) {
    var match = photoKey.match(/(\d+)$/);
    if (match) {
      var nameKey = 'Pet Name ' + match[1];
      if (properties[nameKey]) return properties[nameKey];
    }
    if (properties['Pet Name']) return properties['Pet Name'];
    if (properties['Pet Name 1']) return properties['Pet Name 1'];
    return null;
  }

  /** Render pet photo radio buttons in each upsell card */
  function renderPhotoSelectors() {
    if (petPhotos.length === 0) return;

    var containers = document.querySelectorAll('[data-upsell-photo-selector]');
    containers.forEach(function (ctr) {
      ctr.innerHTML = '';

      var label = document.createElement('p');
      label.className = 'bundle-photo-selector__label';
      label.textContent = 'Select pet photo for this product:';
      ctr.appendChild(label);

      var grid = document.createElement('div');
      grid.className = 'bundle-photo-selector__grid';
      ctr.appendChild(grid);

      petPhotos.forEach(function (photo, index) {
        var wrapper = document.createElement('label');
        wrapper.className = 'bundle-photo-selector__option';

        var radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = ctr.dataset.productHandle + '-pet-photo';
        radio.value = photo.key;
        radio.dataset.photoUrl = photo.url;
        radio.dataset.parentId = photo.id;
        radio.dataset.petName = photo.name || '';
        radio.className = 'bundle-photo-selector__radio';
        if (index === 0) radio.checked = true;

        var img = document.createElement('img');
        var thumbUrl = photo.url;
        // Request thumbnail-sized images from Supabase (displayed at 72px, 2x for retina)
        if (typeof thumbUrl === 'string' && thumbUrl.indexOf('supabase.co/storage') !== -1) {
          thumbUrl = thumbUrl.replace('/object/public/', '/render/image/public/');
          thumbUrl += (thumbUrl.indexOf('?') !== -1 ? '&' : '?') + 'width=160&height=160&resize=cover';
        }
        img.src = thumbUrl;
        img.alt = photo.name || 'Pet photo';
        img.className = 'bundle-photo-selector__img';
        img.width = 80;
        img.height = 80;
        img.loading = 'lazy';

        var name = document.createElement('span');
        name.className = 'bundle-photo-selector__name';
        name.textContent = photo.name || 'Pet ' + (index + 1);

        wrapper.appendChild(radio);
        wrapper.appendChild(img);
        wrapper.appendChild(name);
        grid.appendChild(wrapper);
      });

      ctr.hidden = false;
    });
  }

  /** Handle add-to-cart for an upsell product */
  async function addUpsellToCart(btn) {
    if (btn.disabled) return;
    var card = btn.closest('[data-upsell-card]');
    if (!card) return;

    var variantSelect = card.querySelector('[data-upsell-variant]');
    var variantId = variantSelect ? variantSelect.value : card.dataset.defaultVariantId;
    if (!variantId) return;

    btn.disabled = true;
    var originalText = btn.textContent;
    btn.textContent = 'Adding...';
    setCardError(card, null);

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    // Use parent item's Order ID so all bundle items are linked for fulfillment
    formData.append('properties[Order ID]', sourceOrderId || String(Math.floor(10000000 + Math.random() * 90000000)));

    // Internal analytics linking — same as the main wizard add-to-cart;
    // lets the orders/create webhook resolve the bundle item to the
    // anonymous user. Underscore-prefixed properties are hidden from the
    // customer-facing cart UI. Wrapped defensively so this never blocks
    // the bundle add.
    try {
      if (window.instameUserId) {
        formData.append('properties[_instame_user_id]', window.instameUserId);
      }
      if (window.instameSessionId) {
        formData.append('properties[_instame_session_id]', window.instameSessionId);
      }
      // A/B-test variants → underscore-prefixed line item properties so
      // they're hidden from the customer-facing cart UI but still flow
      // to the orders/create webhook. Mirrors the same logic in
      // assets/product-wizard.js so bundle add-to-cart events are
      // attributable to the active variant.
      var experiments = window.instameExperiments || {};
      Object.keys(experiments).forEach(function (k) {
        formData.append('properties[_' + k + ']', experiments[k]);
      });
    } catch (_) {}

    var selectedRadio = card.querySelector('.bundle-photo-selector__radio:checked');
    if (selectedRadio) {
      var photoPath = selectedRadio.dataset.photoUrl;
      if (photoPath.indexOf(SUPABASE_PHOTO_BASE) === 0) photoPath = photoPath.slice(SUPABASE_PHOTO_BASE.length);
      formData.append('properties[Pet Photo]', photoPath);
      if (selectedRadio.dataset.petName) {
        formData.append('properties[Pet Name]', selectedRadio.dataset.petName);
      }
      formData.append('properties[_parentId]', selectedRadio.dataset.parentId);
    }

    try {
      var res = await fetch('/cart/add.js', { method: 'POST', body: formData });
      var result = await res.json();

      if (result.status) {
        // Shopify rejected the add (e.g. sold out) — surface the reason and
        // track it with the same event/props the wizard uses so it lands in
        // the existing add_to_cart_failed funnel.
        var reason = result.description || result.message || null;
        captureEvent('add_to_cart_failed', {
          source: 'bundle_upsell_shopify_rejected',
          product_id: card.dataset.productId || null,
          variant_id: variantId,
          message: reason
        });
        setCardError(card, reason || 'This item could not be added — please try again.');
        btn.textContent = originalText;
        btn.disabled = false;
        return;
      }

      btn.textContent = 'Added!';
      btn.classList.add('bundle-card__btn--added');

      // Track added_to_bundle in Supabase. We pull variant_title and
      // options from the just-added cart line item so this event carries
      // the same shape as added_to_cart (consistent variant attribution
      // across the funnel).
      try {
        if (window.instameTrack) {
          var productTitle = card.querySelector('.bundle-card__title');
          var priceEl = card.querySelector('[data-upsell-price]');
          var petNameRadio = card.querySelector('.bundle-photo-selector__radio:checked');
          fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (cart) {
              var addedItem = cart && cart.items && cart.items.find(function (it) {
                return String(it.variant_id) === String(variantId);
              });
              var optionsObj = {};
              if (addedItem && Array.isArray(addedItem.options_with_values)) {
                addedItem.options_with_values.forEach(function (o) {
                  if (o && o.name) optionsObj[o.name] = o.value;
                });
              }
              window.instameTrack('added_to_bundle', {
                product_id: card.dataset.productId || null,
                variant_id: variantId,
                metadata: {
                  pet_name: (petNameRadio && petNameRadio.dataset.petName) || null,
                  price: (priceEl && priceEl.textContent) || null,
                  bundle_type: 'post_add_upsell',
                  product_title: (productTitle && productTitle.textContent.trim()) || null,
                  variant_title: (addedItem && addedItem.variant_title) || null,
                  options: optionsObj
                }
              });
            })
            .catch(function () {
              // Fallback to minimal payload if cart fetch fails — same
              // shape we had before the variant_title enhancement.
              window.instameTrack('added_to_bundle', {
                product_id: card.dataset.productId || null,
                variant_id: variantId,
                metadata: {
                  pet_name: (petNameRadio && petNameRadio.dataset.petName) || null,
                  price: (priceEl && priceEl.textContent) || null,
                  bundle_type: 'post_add_upsell',
                  product_title: (productTitle && productTitle.textContent.trim()) || null
                }
              });
            });
        }
      } catch (_) {}

      // Dispatch theme-native cart:update event so cart icon and drawer contents refresh
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          data: {
            source: 'product-form-component',
            itemCount: 1,
          },
        },
      }));

      // Open cart drawer to show the newly added item
      var cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.showDialog === 'function') {
        cartDrawer.showDialog();
      }

      setTimeout(function () {
        btn.textContent = originalText;
        btn.disabled = false;
        btn.classList.remove('bundle-card__btn--added');
      }, 2000);
    } catch (e) {
      console.error('Bundle add-to-cart error:', e);
      captureEvent('add_to_cart_failed', {
        source: 'bundle_upsell_network_error',
        product_id: card.dataset.productId || null,
        variant_id: variantId,
        message: (e && e.message) || String(e)
      });
      setCardError(card, "We couldn't add this item — check your connection and try again.");
      btn.textContent = originalText;
      btn.disabled = false;
    }
  }


  /** Bind event listeners on dynamically rendered cards */
  function bindEvents() {
    // Add-to-cart buttons
    document.querySelectorAll('[data-upsell-add-to-cart]').forEach(function (btn) {
      btn.addEventListener('click', function () { addUpsellToCart(btn); });
    });

    // Variant change — update price and gallery image
    document.querySelectorAll('[data-upsell-variant]').forEach(function (select) {
      select.addEventListener('change', function () {
        var card = select.closest('[data-upsell-card]');
        if (!card) return;

        var option = select.options[select.selectedIndex];

        // Update price
        var priceEl = card.querySelector('[data-upsell-price]');
        if (priceEl && option && option.dataset.price) {
          priceEl.textContent = formatMoney(option.dataset.price);
        }

        // Update compare price
        var comparePriceEl = card.querySelector('.bundle-card__compare-price');
        if (option && option.dataset.comparePrice && parseFloat(option.dataset.comparePrice) > parseFloat(option.dataset.price)) {
          if (!comparePriceEl) {
            comparePriceEl = document.createElement('span');
            comparePriceEl.className = 'bundle-card__compare-price';
            card.querySelector('.bundle-card__price-row').appendChild(comparePriceEl);
          }
          comparePriceEl.textContent = formatMoney(option.dataset.comparePrice);
        } else if (comparePriceEl) {
          comparePriceEl.remove();
        }

        // Scroll gallery to variant's featured image
        var imageId = option ? option.dataset.imageId : '';
        var gallery = card.querySelector('[data-bundle-gallery]');
        if (gallery && imageId) {
          scrollGalleryToImageId(gallery, imageId);
        }
      });
    });

    // Gallery arrow navigation
    document.querySelectorAll('[data-bundle-gallery]').forEach(function (gallery) {
      var prevBtn = gallery.querySelector('[data-gallery-prev]');
      var nextBtn = gallery.querySelector('[data-gallery-next]');

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          var current = parseInt(gallery.getAttribute('data-gallery-index'), 10) || 0;
          setGalleryIndex(gallery, current - 1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          var current = parseInt(gallery.getAttribute('data-gallery-index'), 10) || 0;
          setGalleryIndex(gallery, current + 1);
        });
      }
    });
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
})();
